import React, { useState } from 'react';
import './CostCalculator.css';

// Safe expression calculator for basic arithmetic
const calculateExpression = (expr) => {
  // Remove spaces
  expr = expr.replace(/\s/g, '');
  
  // Handle parentheses recursively
  while (expr.includes('(')) {
    expr = expr.replace(/\(([^()]+)\)/, (match, subExpr) => {
      return calculateExpression(subExpr);
    });
  }
  
  // Handle multiplication and division
  const mdRegex = /(-?\d+\.?\d*)([*/])(-?\d+\.?\d*)/;
  while (mdRegex.test(expr)) {
    expr = expr.replace(mdRegex, (match, a, op, b) => {
      const result = op === '*' ? parseFloat(a) * parseFloat(b) : parseFloat(a) / parseFloat(b);
      return result.toString();
    });
  }
  
  // Handle addition and subtraction
  const asRegex = /(-?\d+\.?\d*)([+-])(-?\d+\.?\d*)/;
  while (asRegex.test(expr)) {
    expr = expr.replace(asRegex, (match, a, op, b) => {
      const result = op === '+' ? parseFloat(a) + parseFloat(b) : parseFloat(a) - parseFloat(b);
      return result.toString();
    });
  }
  
  return parseFloat(expr) || 0;
};

const CostCalculator = ({ title, inputs, showTotal = true, currency = 'EUR', formula = null, note = null }) => {
  const [values, setValues] = useState({});

  const handleInputChange = (inputId, value) => {
    setValues(prev => ({
      ...prev,
      [inputId]: value
    }));
  };

  const calculateTotal = () => {
    if (formula) {
      // Safe formula calculation using basic operations only
      const variables = { ...values };
      
      try {
        // Replace variables with their values
        let processedFormula = formula.replace(/\b(\w+)\b/g, (match) => {
          const value = variables[match] || 0;
          return parseFloat(value) || 0;
        });
        
        // Only allow basic arithmetic operations
        if (!/^[0-9+\-*/.\s()]+$/.test(processedFormula)) {
          return 0;
        }
        
        // Use safe expression calculator
        return calculateExpression(processedFormula);
      } catch (e) {
        console.warn('Formula calculation error:', e);
        return 0;
      }
    }

    // Default: sum all input values
    return inputs.reduce((total, input) => {
      const value = values[input.id] || (input.type === 'fixed' ? input.value : 0);
      return total + parseFloat(value) || 0;
    }, 0);
  };

  const renderInput = (input) => {
    const currentValue = values[input.id] || (input.type === 'fixed' ? input.value : input.default || input.min || 0);

    switch (input.type) {
      case 'fixed':
        return (
          <div className="input-display">
            {currency} {input.value}
          </div>
        );
      
      case 'range':
        return (
          <div className="range-input">
            <input
              type="range"
              min={input.min}
              max={input.max}
              value={currentValue}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              className="range-slider"
            />
            <div className="range-value">
              {currency} {currentValue}
            </div>
          </div>
        );
      
      case 'number':
        return (
          <input
            type="number"
            min={input.min}
            max={input.max}
            value={currentValue}
            onChange={(e) => handleInputChange(input.id, e.target.value)}
            className="number-input"
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="cost-calculator">
      {title && <h3 className="calculator-title">{title}</h3>}
      
      <div className="calculator-inputs">
        {inputs.map((input) => (
          <div key={input.id} className="calculator-input-row">
            <label className="input-label">{input.label}</label>
            {renderInput(input)}
          </div>
        ))}
      </div>

      {showTotal && (
        <div className="calculator-total">
          <div className="total-label">Total:</div>
          <div className="total-value">
            {currency} {calculateTotal().toFixed(2)}
          </div>
        </div>
      )}

      {note && (
        <div className="calculator-note">
          {note}
        </div>
      )}
    </div>
  );
};

export default CostCalculator;
